import { INITIAL_EVENTS } from '../core/sampleData.ts';
import { DEFAULT_RULE_CONFIG } from '../core/linter.ts';
import { EventItem, EventTemplate, RuleConfig } from '../core/types.ts';

const LOCAL_STORAGE_KEY = 'oweeks_events_v1';
const TEMPLATES_KEY = 'oweeks_templates_v1';
const AUTH_TOKEN_KEY = 'oweeks_auth_token';
const RULE_CONFIG_KEY = 'oweeks_rule_config_v1';
const API_URL = '/api/events';
const TEMPLATES_API_URL = '/api/templates';

export function getAuthToken(): string {
  const localToken = localStorage.getItem(AUTH_TOKEN_KEY);
  if (localToken) return localToken;

  if (typeof window !== 'undefined' && window.location) {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('key') || params.get('token');
    if (urlToken) {
      localStorage.setItem(AUTH_TOKEN_KEY, urlToken);
      return urlToken;
    }
  }
  return '';
}

export function setAuthToken(token: string) {
  if (token) {
    localStorage.setItem(AUTH_TOKEN_KEY, token.trim());
  } else {
    localStorage.removeItem(AUTH_TOKEN_KEY);
  }
}

export function clearAuthToken() {
  localStorage.removeItem(AUTH_TOKEN_KEY);
}

function getAuthHeaders(extraHeaders: Record<string, string> = {}): HeadersInit {
  const headers: Record<string, string> = { ...extraHeaders };
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export async function loadEvents(): Promise<EventItem[]> {
  try {
    const res = await fetch(API_URL, {
      headers: getAuthHeaders(),
    });
    if (res.status === 401) {
      throw new Error('UNAUTHORIZED');
    }
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(data));
        return data;
      }
    }
  } catch (err: any) {
    if (err?.message === 'UNAUTHORIZED') {
      throw err;
    }
    console.warn('Could not load events from server API, falling back to localStorage:', err);
  }

  // Fallback to localStorage
  const localStr = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (localStr) {
    try {
      const parsed = JSON.parse(localStr);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    } catch (_e) {
      // invalid cache
    }
  }

  // Final fallback to INITIAL_EVENTS
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(INITIAL_EVENTS));
  return INITIAL_EVENTS;
}

export async function saveEvents(events: EventItem[]): Promise<boolean> {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(events));

  try {
    const res = await fetch(API_URL, {
      method: 'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(events),
    });
    return res.ok;
  } catch (err) {
    console.warn('Could not save events to server API:', err);
    return false;
  }
}

export async function loadTemplates(): Promise<EventTemplate[]> {
  try {
    const res = await fetch(TEMPLATES_API_URL, {
      headers: getAuthHeaders(),
    });
    if (res.status === 401) {
      throw new Error('UNAUTHORIZED');
    }
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data)) {
        localStorage.setItem(TEMPLATES_KEY, JSON.stringify(data));
        return data;
      }
    }
  } catch (err: any) {
    if (err?.message === 'UNAUTHORIZED') {
      throw err;
    }
    console.warn('Could not load templates from server API:', err);
  }

  const localStr = localStorage.getItem(TEMPLATES_KEY);
  if (localStr) {
    try {
      return JSON.parse(localStr);
    } catch (_e) {
      //
    }
  }

  return [];
}

export function exportEventsJSON(events: EventItem[]) {
  const jsonStr = JSON.stringify(events, null, 2);
  const blob = new Blob([jsonStr], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `oweeks_schedule_2026_${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function parseImportJSON(file: File): Promise<EventItem[]> {
  const text = await file.text();
  const parsed = JSON.parse(text);
  if (!Array.isArray(parsed)) {
    throw new Error('Imported JSON must be an array of event objects.');
  }

  for (const item of parsed) {
    if (!item.id || !item.title || !item.start || !item.end) {
      throw new Error('One or more events in the JSON file are missing required properties (id, title, start, end).');
    }
  }

  return parsed as EventItem[];
}

export function loadRuleConfig(): RuleConfig {
  if (typeof localStorage === 'undefined') return DEFAULT_RULE_CONFIG;
  const localStr = localStorage.getItem(RULE_CONFIG_KEY);
  if (localStr) {
    try {
      const parsed = JSON.parse(localStr);
      return {
        ...DEFAULT_RULE_CONFIG,
        ...parsed,
        categoryMaxRatioThresholds: {
          ...DEFAULT_RULE_CONFIG.categoryMaxRatioThresholds,
          ...(parsed.categoryMaxRatioThresholds || {}),
        },
      };
    } catch (_e) {
      // fallback to default
    }
  }
  return DEFAULT_RULE_CONFIG;
}

export function saveRuleConfig(config: RuleConfig): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(RULE_CONFIG_KEY, JSON.stringify(config));
}
